import React, { useMemo, useState } from "react";
import { AiOutlineEye, AiOutlineEyeInvisible } from "react-icons/ai";
import { MdFilterList } from "react-icons/md";
import { formatAmount } from "../../utils/formatTime";
import {
  LIVE_ACTIVITY_TYPES,
  getActivityTypeConfig,
  withDefaultSubtype,
  type ActivityFilter,
  type ActivityType,
} from "../../utils/activity/filters";
import {
  extractSummaryTotalUSD,
  useTransactionsSummary,
} from "../../hooks/queries/useTransactions";
import ActivityTypeTabBar from "./ActivityTypeTabBar";
import ActivityTypePanel from "./ActivityTypePanel";
import ActivityFilterPanel, {
  DEFAULT_SHEET_FILTERS,
  type ActivitySheetFilters,
} from "./ActivityFilterPanel";

const ROUTES = LIVE_ACTIVITY_TYPES.map((t) => ({ key: t.key, label: t.label }));

/**
 * Activity page orchestrator — the state model is ported from mobile's
 * ActivityBody: subtype is remembered per type, while status and date range are
 * shared across every tab.
 */
const ActivityBody = ({
  onSelect,
}: {
  onSelect: (raw: any) => void;
}) => {
  const [index, setIndex] = useState(0);
  const [visible, setVisible] = useState(true);
  const [filterOpen, setFilterOpen] = useState(false);
  const [subtypeByType, setSubtypeByType] = useState<
    Partial<Record<ActivityType, string | undefined>>
  >(() => {
    const seed: Partial<Record<ActivityType, string | undefined>> = {};
    LIVE_ACTIVITY_TYPES.forEach((t) => {
      seed[t.key] = withDefaultSubtype(t.key).subtype;
    });
    return seed;
  });
  const [sheetFilters, setSheetFilters] =
    useState<ActivitySheetFilters>(DEFAULT_SHEET_FILTERS);

  const activeType = ROUTES[index].key as ActivityType;

  // One stable ActivityFilter per type.
  const sceneFilters = useMemo(() => {
    const map = {} as Record<ActivityType, ActivityFilter>;
    LIVE_ACTIVITY_TYPES.forEach((t) => {
      map[t.key] = {
        type: t.key,
        subtype: subtypeByType[t.key],
        // Payouts display failed as "pending", so a FAILED selection shared
        // from another tab must not filter payouts by an invisible state.
        status:
          t.key === "payout" && sheetFilters.status === "FAILED"
            ? undefined
            : sheetFilters.status,
        startDate: sheetFilters.startDate,
        endDate: sheetFilters.endDate,
      };
    });
    return map;
  }, [subtypeByType, sheetFilters]);

  const filter = sceneFilters[activeType];
  const typeConfig = getActivityTypeConfig(activeType);
  const subtypeLabel = typeConfig?.subtypes?.find(
    (s) => s.key === filter.subtype
  )?.label;

  const { data: summary, isPending: summaryPending } =
    useTransactionsSummary(filter);
  const totalUSD = extractSummaryTotalUSD(summary);
  const summaryDisplay = !visible
    ? "*****"
    : totalUSD == null
    ? summaryPending
      ? "$ --"
      : "$0.00"
    : `$${formatAmount(totalUSD)}`;
  const summaryLabel = `Total · ${typeConfig?.label ?? ""}${
    subtypeLabel ? ` ${subtypeLabel}` : ""
  }`;

  const subtypeIsDefault =
    filter.subtype === withDefaultSubtype(activeType).subtype;
  const activeFilterCount =
    (sheetFilters.status ? 1 : 0) +
    (sheetFilters.datePreset !== "all" ? 1 : 0) +
    (subtypeIsDefault ? 0 : 1);

  const onApplyFilters = (
    filters: ActivitySheetFilters,
    subtype?: string
  ) => {
    setSheetFilters(filters);
    setSubtypeByType((prev) => ({ ...prev, [activeType]: subtype }));
    setFilterOpen(false);
  };

  return (
    <>
      <div className="w-full bg-white dark:bg-[#1F2C36] rounded-t-xl px-4 py-3">
        <h4 className="text-gray-500 dark:text-gray-400 text-[13px] font-bold">
          {summaryLabel}
        </h4>
      </div>
      <div className="w-full bg-[#F1F3F5] dark:bg-[#1C2227] rounded-b-xl px-4 py-6 flex justify-between items-center">
        <div className="flex justify-start items-center gap-4">
          <h4 className="text-[#042741] dark:text-gray-300 text-[16px] font-semibold">
            {summaryDisplay}
          </h4>
          <button
            onClick={() => setVisible((prev) => !prev)}
            aria-label={visible ? "Hide amounts" : "Show amounts"}
          >
            {visible ? (
              <AiOutlineEyeInvisible className="text-[#869AA9] dark:text-white text-[20px]" />
            ) : (
              <AiOutlineEye className="text-[#869AA9] dark:text-white text-[20px]" />
            )}
          </button>
        </div>
        <button
          onClick={() => setFilterOpen(true)}
          className="relative flex items-center gap-1 px-3 py-2 rounded-lg bg-white dark:bg-[#1F2C36] text-[13px] font-semibold text-[#042741] dark:text-gray-300"
        >
          <MdFilterList className="text-[16px]" />
          Filter
          {activeFilterCount > 0 && (
            <span className="absolute -top-1 -right-1 w-[16px] h-[16px] rounded-full bg-secondary_button text-white text-[9px] font-bold flex justify-center items-center">
              {activeFilterCount}
            </span>
          )}
        </button>
      </div>

      <ActivityTypeTabBar routes={ROUTES} index={index} onChange={setIndex} />

      {/* Only the active tab mounts; the others stay warm in the query cache. */}
      <ActivityTypePanel
        key={activeType}
        filter={filter}
        hideAmounts={!visible}
        onSelect={onSelect}
      />

      <ActivityFilterPanel
        open={filterOpen}
        value={sheetFilters}
        onApply={onApplyFilters}
        onClose={() => setFilterOpen(false)}
        type={activeType}
        subtype={filter.subtype}
      />
    </>
  );
};

export default ActivityBody;
